import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import RoleService from '../../services/user-role/role-service';
import AssignRoleDialog from './role-assignment-dialog';
import { UserRolesSummaryDTO } from '../../models/user-role/role-model';

interface AssignRoleTableProps {
    data: UserRolesSummaryDTO[];
    totalRecords: number;
    rows: number;
    first: number;
    onPage: (event: any) => void;
    // FIXED: Proper TypeScript type for PrimeReact DataTable
    sortField?: string | null;
    sortOrder?: -1 | 0 | 1 | null;
    onSort: (event: any) => void;
    onRefresh: () => void;
}

const AssignRoleTable: React.FC<AssignRoleTableProps> = ({
    data, totalRecords, rows, first, onPage, sortField, sortOrder, onSort, onRefresh
}) => {
    const toast = useRef<Toast>(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedRole, setSelectedRole] = useState<UserRolesSummaryDTO | null>(null);

    const menu = useRef<Menu>(null);
    const actionRowData = useRef<UserRolesSummaryDTO | null>(null);

    const handleEdit = (rowData: UserRolesSummaryDTO) => {
        setSelectedRole(rowData);
        setShowEditDialog(true);
    };

    const confirmDelete = (rowData: UserRolesSummaryDTO) => {
        confirmDialog({
            message: `Are you sure you want to delete the role '${rowData.roleName}' from user '${rowData.userName}'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => handleDelete(rowData),
            reject: () => { }
        });
    };

    const handleDelete = async (rowData: UserRolesSummaryDTO) => {
        try {
            await RoleService.removeRoleFromUser(rowData.userId, rowData.roleId);
            toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Role deleted successfully', life: 3000 });
            onRefresh();
        } catch (error: any) {
            console.error("Error deleting role", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to delete role', life: 3000 });
        }
    };

    const menuItems: MenuItem[] = [
        {
            label: 'Edit',
            icon: 'pi pi-pencil',
            command: () => {
                if (actionRowData.current) {
                    handleEdit(actionRowData.current);
                }
            }
        },
        {
            label: 'Delete',
            icon: 'pi pi-trash',
            className: 'text-red-500',
            command: () => {
                if (actionRowData.current) {
                    confirmDelete(actionRowData.current);
                }
            }
        }
    ];

    const actionTemplate = (rowData: UserRolesSummaryDTO) => {
        return (
            <div className="flex justify-content-center">
                <Button
                    icon="pi pi-ellipsis-v"
                    className="p-button-rounded p-button-text p-button-secondary"
                    onClick={(e) => {
                        actionRowData.current = rowData;
                        menu.current?.toggle(e);
                    }}
                    tooltip="Options"
                />
            </div>
        );
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const dateValue = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
        const date = new Date(dateValue);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' +
            date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    // FIXED: Proper TypeScript conversion for sortOrder
    const getSortOrderForTable = (): -1 | 0 | 1 | undefined => {
        if (sortOrder === null || sortOrder === undefined) return undefined;
        return sortOrder as -1 | 0 | 1;
    };

    return (
        <div className="assignment-table-card">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Menu model={menuItems} popup ref={menu} />

            <DataTable
                value={data}
                lazy
                paginator
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPage={onPage}
                // FIXED: Type-safe sort props (no more TS error)
                sortField={sortField || undefined}
                sortOrder={getSortOrderForTable()}
                onSort={onSort}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '50rem' }}
                stripedRows
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords} records">
                <Column field="userName" header="User Name" sortable style={{ width: '20%' }}></Column>
                <Column field="roleName" header="Role Name" sortable style={{ width: '15%' }}></Column>
                <Column field="modifiedByName" header="Modified By" sortable style={{ width: '15%' }}></Column>
                <Column field="modifiedDate" header="Modified Date" body={(rowData) => formatDate(rowData.modifiedDate)} sortable style={{ width: '15%' }}></Column>
                <Column body={actionTemplate} header="Actions" alignHeader="center" style={{ width: '10%', textAlign: 'center' }}></Column>
            </DataTable>

            {selectedRole && (
                <AssignRoleDialog
                    visible={showEditDialog}
                    onHide={() => { setShowEditDialog(false); setSelectedRole(null); }}
                    onSaveSuccess={onRefresh}
                    editMode={true}
                    initialData={{ userId: selectedRole.userId, roleId: selectedRole.roleId, userName: selectedRole.userName }}
                    toast={toast}
                />
            )}
        </div>
    );
};

export default AssignRoleTable;
